import { memo, ReactElement, useCallback, useMemo } from 'react';
import { NumberRangeFilterProps, NumberRangeFilter } from './NumberRangeFilter';
import { FilterNumberRangeOptionDTO, FloatFilterDTO } from '../../../api/generated';
import { skipUndefined } from '../../../utils';

export interface ScaledNumberRangeFilterProps extends NumberRangeFilterProps {
  factor?: number;
}

export const ScaledNumberRangeFilter = memo(function ({
  factor = 1,
  value,
  onValueChange,
  filterOptions,
  ...restProps
}: ScaledNumberRangeFilterProps): ReactElement {
  const handleValueChange = useCallback(
    function (newValue?: FloatFilterDTO) {
      const gte = skipUndefined(newValue?.range?.gte, (v) => v * factor);
      const lte = skipUndefined(newValue?.range?.lte, (v) => v * factor);
      onValueChange(lte === undefined && gte === undefined ? undefined : { range: { gte, lte } });
    },
    [factor, onValueChange]
  );

  const scaledValue: FloatFilterDTO = useMemo(
    () => ({
      range: {
        gte: skipUndefined(value?.range?.gte, (v) => v / factor),
        lte: skipUndefined(value?.range?.lte, (v) => v / factor),
      },
    }),
    [value, factor]
  );

  const scaledFilterOptions: FilterNumberRangeOptionDTO | undefined = useMemo(
    () =>
      skipUndefined(filterOptions, (v) => ({
        min: v.min / factor,
        max: v.max / factor,
      })),
    [filterOptions, factor]
  );

  return (
    <NumberRangeFilter
      {...restProps}
      onValueChange={handleValueChange}
      value={scaledValue}
      filterOptions={scaledFilterOptions}
    />
  );
});
